import { isEmpty, pick, set } from 'lodash';
import React from 'react';
import { BBCommonProps, BBUiConfig } from 'types/story';
import { FieldBBErrorType, FieldBBType } from 'client/components/common/BuildingBlocks/Fields/constants';
import { StoryCard } from '../StoryCard';
import { FormContext } from './FormContext';
import { initialState, RegisterFieldFnType, State, ValidateFnType, ValidationResultType } from './types';
import { validations } from './validations';

type ShouldValidateProps = {
	componentProps: BBUiConfig['componentProps'];
	errorType: FieldBBErrorType;
};

function shouldValidate({ errorType, componentProps }: ShouldValidateProps) {
	const { fieldType, fieldRequired, fieldFullName } = componentProps;

	switch (errorType) {
		case FieldBBErrorType.required:
			return !!fieldRequired;
		case FieldBBErrorType.fullName:
			return !!fieldFullName;
		case FieldBBErrorType.email:
			return fieldType === FieldBBType.email;
		case FieldBBErrorType.tel:
			return fieldType === FieldBBType.tel;
		default:
			return false;
	}
}

class FormCard extends StoryCard {
	state: State = { ...initialState };

	fields: Record<string, BBCommonProps> = {};

	get validationResult(): ValidationResultType {
		return pick(this.state, ['isValid', 'errors']);
	}

	get providerValues() {
		return {
			validate: this.validate,
			registerField: this.registerField,
			...this.validationResult,
		};
	}

	registerField: RegisterFieldFnType = field => {
		this.fields[field._id] = field;
	};

	validate: ValidateFnType = async formData => {
		type FieldId = string;
		const errors: Record<FieldId, FieldBBErrorType[]> = {};

		// Validate
		Object.entries(formData).forEach(([fieldId, fieldData]) => {
			const field = this.fields[fieldId];

			if (!field) {
				/*
				 NOTE:
				 Expected that "global" fields doesn't have Form card context to register itself.
				 Other fields must be listed in "this.fields"

				 "global" fields won't be handled by this component.
				 It's expected behaviour, because there is no sense in "global" fields.
				 If sometime it would be needed to validate "global" fields,
				 think about how we can provide context of this component to higher components like <StoryElements />
				 */

				return;
			}

			const { componentProps } = field.uiConfig;

			(Object.keys(componentProps.fieldError ?? {}) as FieldBBErrorType[]).forEach(errorType => {
				const isValid = shouldValidate({ errorType, componentProps })
					? validations[errorType](
							fieldData.type === 'checkbox' ? fieldData.attributes?.checked ?? false : fieldData.data
					  )
					: true;

				if (!isValid) {
					if (!errors[fieldId]) {
						set(errors, fieldId, []);
					}
					errors[fieldId].push(errorType);
				}
			});
		});

		const hasErrors = !isEmpty(errors);

		await this.setState(hasErrors ? { isValid: false, errors } : initialState);

		return this.validationResult;
	};

	onSubmit = event => {
		event.preventDefault();
	};

	render() {
		return (
			<div className="form-card">
				<form autoComplete="off" onSubmit={this.onSubmit}>
					<FormContext.Provider value={this.providerValues}>{super.renderCard()}</FormContext.Provider>
				</form>
			</div>
		);
	}
}

export default FormCard;
